import { Fade, LinearProgress, linearProgressClasses } from "@mui/material";
import { useIsFetching } from "@tanstack/react-query";
import clsx from "clsx";
import { CSSProperties, ComponentType, ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { twMerge } from "tailwind-merge";
import { useTreeViewOpenedFolders } from "../../controllers/TreeViewController/hooks/treeViewFolderHooks";
import { useHandleScroll, useInitTreeView } from "../../controllers/TreeViewController/hooks/treeViewItemsHooks";
import type { TreeViewListItem } from "../../controllers/TreeViewController/types/ListItem";
import Loading from "../Loading";
import NoContents from "../NoContents";
import FoldersListItem from "./components/FoldersListItem";
import TreeViewHeader from "./components/TreeViewHeader";
import { ITEM_SIZE } from "./constants/FolderListItemConstants";

const TreeViewList = ({
	ListItemComponent,
	preContentSection,
	canCloseSelection = true,
	noContentDescription,
	customHeader,
	className,
	noContentsComponent,
	onToggleAllSelection,
	hideHeader,
	ItemSize = ITEM_SIZE,
}: {
	ListItemComponent: ComponentType<{ item: TreeViewListItem }>;
	preContentSection?: ReactNode;
	canCloseSelection?: boolean;
	noContentDescription?: string;
	customHeader?: (props: { selectAllCheckbox: ReactNode; viewSwitchButton: ReactNode }) => ReactNode;
	noContentsComponent?: ReactNode;
	className?: string;
	onToggleAllSelection?: (areFavorite: boolean) => void;
	hideHeader?: boolean;
	ItemSize?: number;
}) => {
	const { t } = useTranslation();
	const openedFolders = useTreeViewOpenedFolders();
	const { listRef, restoreScroll, keepScroll } = useHandleScroll();

	const { isLoading } = useInitTreeView();

	const renderCallback = useCallback(
		({ index, style, data }: { index: number; style: CSSProperties; data: typeof openedFolders }) => {
			const renderedElement = data[index];

			return (
				<FoldersListItem
					style={style}
					key={renderedElement.id}
					item={renderedElement.item}
					ListItemComponent={ListItemComponent}
				/>
			);
		},
		[ListItemComponent]
	);

	const isFetchingTreeView = useIsFetching({ queryKey: ["treeView"] });

	return (
		<div className={twMerge(clsx("w-full flex-1 overflow-hidden flex flex-col"), className)}>
			{!hideHeader && (
				<TreeViewHeader
					canCloseSelection={canCloseSelection}
					customHeader={customHeader}
					onToggleAllSelection={onToggleAllSelection}
				/>
			)}
			<Fade in={isFetchingTreeView > 0}>
				<LinearProgress
					color="secondary"
					sx={{
						[`& .${linearProgressClasses.bar}`]: {
							backgroundColor: (theme) => `${theme.palette.secondary.main} !important`,
						},
					}}
				/>
			</Fade>
			{preContentSection}
			<div className="w-full flex-1 flex flex-col md:block">
				{isLoading ? (
					<Loading title={t("folders.loadingItems")} sx={{ height: "100%", flex: 1 }} />
				) : openedFolders.length === 0 ? (
					noContentsComponent ?? (
						<div className="h-full flex items-center justify-center flex-1">
							<NoContents variant="document" title={noContentDescription ?? t("folders.stillNoElement")} />
						</div>
					)
				) : (
					<AutoSizer>
						{({ width, height }) => (
							<FixedSizeList
								onScroll={() => keepScroll(listRef.current)}
								outerRef={restoreScroll}
								height={height}
								itemCount={openedFolders.length}
								itemSize={ItemSize}
								width={width}
								itemData={openedFolders}
								layout="vertical"
								className="horizontal-scroll scroll-smooth"
							>
								{renderCallback}
							</FixedSizeList>
						)}
					</AutoSizer>
				)}
			</div>
		</div>
	);
};
export default TreeViewList;
